import { useRouter } from 'next/router';
import { defineMessage, useIntl } from 'react-intl';

import type {
  ProductCardProps,
  ProductCardSpecificProps,
} from '@packages/modules/src/ProductCard/types';
import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters';

import type { EfficiencyFlagProps, FlagProps } from '@packages/shared';
import { useDeviceType } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type {
  EfficiencyFlagType,
  StandardFlagType,
} from '@packages/gql/generated/shopping/graphql';
import { useProductCardPrice } from '../../hooks/useProductCardPrice';
import type { DesktopDisplayMode } from '../../types';
import { mapProductCardAvailabilities } from '../../utils/mapProductCardAvailabilities';

export const SearchProductCardFullVariationGroupFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardFullVariationGroupFragment on CompleteVariationGroup {
    groupName
    image
    imageForHighlight
    sku # only for tracking?
    articleNumber # only for tracking
    href
    price {
      ...SearchProductPriceFragment
    }
    availability {
      ...ProductAvailabilityOverlayFragment
    }
  }
`);

const message = defineMessage({
  id: 'search.productCard.datasheet',
  defaultMessage: 'Produktdatenblatt',
  description: 'Text for the clickable link in a card that opens the datasheet in a modal.',
});

export const SearchProductCardItemFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardFragment on StylePreviewItem {
    styleId # needed?
    styleName
    categoryName # only for tracking
    # isHighlight
    brand {
      name
    }
    customFlag {
      # description
      image
    }
    standardFlags {
      type
      text
    }
    efficiencyFlags {
      type
      text
      legacyColor
      details {
        description
        image
      }
    }
    ratings {
      averageValue
      count
    }
    datasheet
    sellingPoints
    primaryVariationGroup {
      sku
      ...SearchProductCardFullVariationGroupFragment
      ...SearchProductCardClickTrackingVariationGroupFragment
      ...SearchProductCardGlycerinTrackingVariationGroupFragment
    }
    variationGroups {
      groupName
      icon {
        image
      }
    }
    ...SearchProductCardImpressionTrackingItemFragment
    ...SearchProductCardClickTrackingItemFragment
    ...SearchProductCardGlycerinTrackingItemFragment
  }
`);

const getVariationImage = (
  imageUrl: string,
  highlightArticleImageUrl: string | null,
  displayMode: DesktopDisplayMode | undefined,
  isDesktop: boolean,
): string => {
  // if there is no highlight image url, show normal image
  if (!highlightArticleImageUrl) return imageUrl;

  // show regular image if product card is in "Mobile Listenansicht" or "Tablet Listenansicht" mode
  return !isDesktop && displayMode === 'moreInformation' ? imageUrl : highlightArticleImageUrl;
};

const standardFlagTypeMap: Record<StandardFlagType, FlagProps['type']> = {
  DEFAULT: 'neutral',
  SECONDARY: 'secondary',
  SUSTAINABILITY: 'sustainability',
};

const efficiencyFlagTypeMap: Record<EfficiencyFlagType, EfficiencyFlagProps['type']> = {
  ENERGY: 'energyEfficiency',
  ENERGY_OLD: 'energyEfficiencyOld',
  FUEL: 'fuelEfficiency',
  EXTERNAL_ROLLING_NOISE: 'externalRollingNoise',
  WET_SURFACE_GRIP: 'wetGrip',
};

const labelColorsMap = {
  A: 'darkgreen',
  B: 'mediumgreen',
  C: 'lightgreen',
  D: 'yellow',
  E: 'lightorange',
  F: 'darkorange',
  G: 'red',
} as const;

const validColors = Object.values(labelColorsMap);
const isValidColor = (
  color: string,
): color is (typeof labelColorsMap)[keyof typeof labelColorsMap] =>
  validColors.includes(color as (typeof validColors)[number]);

export const useVariationDataForProductCard = (
  maskedItem: FragmentType<typeof SearchProductCardItemFragment>,
  activeVariationIndex: number,
  maskedVariation: FragmentType<typeof SearchProductCardFullVariationGroupFragment>,
  displayMode: DesktopDisplayMode,
  useUnitPriceSpacer?: boolean,
): ProductCardSpecificProps => {
  const item = getFragmentData(SearchProductCardItemFragment, maskedItem);
  const fullVariation = getFragmentData(
    SearchProductCardFullVariationGroupFragment,
    maskedVariation,
  );

  const { query } = useRouter();
  const { formatMessage } = useIntl();

  const { isDesktop } = useDeviceType();

  // remove 'categories' and 'searchquery' from query object to avoid passing them to URL
  const { categories, searchquery, ...filteredQuery } = query;

  const productLink = mergeQueryParameters(fullVariation.href, filteredQuery);

  const customFlag = item.customFlag
    ? [{ type: 'custom' as const, src: item.customFlag.image }]
    : [];
  const standardFlags = item.standardFlags.map(({ type, text }) => ({
    type: standardFlagTypeMap[type],
    text,
  }));
  const flags = ([] as ProductCardProps['flags'])?.concat(customFlag)?.concat(standardFlags);

  const additionalFlags = item.efficiencyFlags?.map(
    ({ type, text, legacyColor, details }) =>
      ({
        type: efficiencyFlagTypeMap[type],
        text,
        labelColor:
          type === 'ENERGY_OLD' && legacyColor && isValidColor(legacyColor)
            ? legacyColor
            : labelColorsMap[text as keyof typeof labelColorsMap], // undefined if invalid color, which is fine as the labelColor is optional
        overlay: details ? { title: details.description, imgUrl: details.image } : undefined,
      }) satisfies EfficiencyFlagProps,
  );

  return {
    sku: fullVariation.sku,
    brand: item.brand.name,
    title: item.styleName,
    colors: item.variationGroups.map((variationGroup, index) => ({
      name: variationGroup.groupName ?? '',
      image: variationGroup.icon?.image,
      active: index === activeVariationIndex,
    })),
    availability: mapProductCardAvailabilities(fullVariation.availability),
    variationName: fullVariation.groupName ?? undefined,
    productLink,
    image: getVariationImage(
      fullVariation.image,
      fullVariation.imageForHighlight,
      displayMode,
      isDesktop,
    ),
    price: useProductCardPrice(fullVariation.price, { useUnitPriceSpacer }),
    datasheet: item.datasheet ? { url: item.datasheet, name: formatMessage(message) } : undefined,
    flags,
    additionalFlags,
    additionalInfo: displayMode === 'moreInformation' ? item.sellingPoints : undefined,
    rating: item.ratings
      ? { avgRating: item.ratings.averageValue, reviewCount: item.ratings.count }
      : undefined,
  };
};
